import { useState } from 'react'
import { Navigate } from 'react-router-dom'

import { useMe } from '../../hooks/queries'
import { startOffen, startziel } from '../../lib/letzterChat'
import { useTexte } from '../../texte'

/**
 * Beim Öffnen der Anwendung: dorthin zurück, wo man war.
 *
 * Drei Fälle, und alle drei stehen hier:
 *
 * - **Zuletzt ein Chat** — der geht auf.
 * - **Zuletzt die Übersicht** — dann bleibt es die Übersicht. Wer die Liste
 *   offen gelassen hat, will sie wiedersehen.
 * - **Nichts gemerkt** — dann die Übersicht. Also beim ersten Start auf einem
 *   Gerät, nach dem Abmelden und dann, wenn die gemerkte Reise verschwunden
 *   ist.
 *
 * Zwischendurch stand hier ein vierter Fall: ohne Erinnerung den obersten
 * Eintrag der Liste aufmachen. Das war ein Pflaster, als die `StandWache` die
 * Erinnerung überschrieb (siehe `Shell`) — und ein schlechtes: es zeigt einen
 * Chat, den niemand zuletzt offen hatte, und verdeckt dabei genau den Zustand,
 * an dem man den Fehler erkannt hätte.
 *
 * Und das **einmal je Start**, nicht bei jedem Besuch der Übersicht. Sonst
 * wäre der Weg zur Liste versperrt: jedes „Zurück" fiele sofort wieder in den
 * Chat, aus dem man gerade gekommen ist.
 *
 * **Und „Start" heißt der erste Weg der Sitzung, nicht der erste Blick auf
 * diese Seite** (1.84.1). Die Marke stand bis dahin hier im Modul und wurde
 * erst verbraucht, wenn diese Seite zum ersten Mal gezeichnet wurde. Eine
 * Sitzung, die im Chat beginnt (iOS nimmt die letzte Adresse wieder auf, eine
 * Benachrichtigung öffnet `/t/8?m=…`, die `StandWache` lädt im Chat neu),
 * zeichnete sie nie -- und der erste Tipp auf den Koffer sah dann aus wie der
 * Start und fiel in den Chat zurück. Aus dem Betrieb: „führt nicht immer
 * sofort zur Reiseliste zurück … nicht reproduzierbar." Verbraucht wird der
 * Start jetzt in `Shell`, an der jeder Weg vorbeikommt; hier wird nur gelesen.
 */

/** Was rechts steht, solange kein Chat geöffnet ist. Am Telefon nie sichtbar. */
export function StartPane() {
  const texte = useTexte()
  const t = texte.rahmen.start
  const me = useMe()
  // Im Anfangswert wird nur gelesen, nicht verbraucht: React ruft ihn in der
  // Entwicklung zweimal auf. Verbraucht wird in `Shell`, nach diesem Zeichnen.
  const [start] = useState(() => startOffen())
  const [ziel] = useState(() => (startOffen() ? startziel() : null))

  // Kein `replace`: der Schritt bleibt in der Geschichte des Browsers stehen,
  // damit der Zurück-Knopf zur Liste führt und nicht aus der Anwendung heraus.
  if (start && ziel?.art === 'chat') return <Navigate to={ziel.pfad} />

  return (
    <div className="empty">
      <p className="empty__title">{texte.allgemein.name}</p>
      {me.data?.may_create_trips ? (
        <p>
          {t.waehleMitAnlegen}
          <br />
          {t.alsAutor}
        </p>
      ) : (
        <p>
          {t.waehle}
          <br />
          {t.alsLeser}
        </p>
      )}
    </div>
  )
}
